import { ButtonHTMLAttributes, DetailedHTMLProps, ReactNode } from "react";
import styled from "@emotion/styled";
import theme from "@monorepo/styles/theme";
import CircularProgress from "@mui/material/CircularProgress";

export const SButton = styled("button")<TButtonProps>(({ isLink, isActive, size, variant }) => ({
  ...(isLink && {
    font: "inherit",
    outline: 0,
    border: 0,
    padding: 0,
    cursor: "pointer",
    textDecoration: "underline",
    background: "none",
    transition: "color 0.3s ease",
    "&:hover": {
      color: theme.palette.nobelGray.main,
    },
  }),
  ...(!isLink && {
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "bold",
    borderRadius: theme.spacing(3 / 4),
    border: 0,
    borderBottom: `${theme.spacing(3 / 8)} solid transparent`,
    backgroundColor: "transparent",
    color: theme.palette.secondary.main,
    cursor: "pointer",
    transition: "0.2s ease-in background-color",
    ":focus:not(:focus-visible)": {
      outline: "none",
    },
    "&:focus-visible": {
      outline: 0,
    },
    "&:disabled": {
      pointerEvents: "none",
      opacity: 0.4,
    },
    ...(variant === "primary" && {
      backgroundColor: theme.palette.primary.main,
      "&:hover,&:focus-visible": {
        backgroundColor: `lighten(${theme.palette.mysticGray.main}, 5%)`,
      },
      "&:focus": {
        borderBottom: `${theme.spacing(3 / 8)} solid ${theme.palette.primary.main}`,
      },
    }),
    ...(variant === "secondary" && {
      backgroundColor: theme.palette.secondary.dark,
      color: theme.palette.blueBayoux.main,
      border: `1px solid ${theme.palette.secondary.dark}`,
      transition: "0s ease-in background-color",
      "&:hover,&:focus-visible": {
        backgroundColor: `darken(${theme.palette.secondary.dark}, 5%)`,
      },
    }),
    ...(variant === "outlined" && {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.text.disabled,
      border: "none",
      transition: "0s ease-in background-color",
      boxShadow: `inset 0px 1px 1px ${theme.palette.mysticGray.main}`,
      "&:hover,&:focus-visible": {
        backgroundColor: `lighten(${theme.palette.mysticGray.main}, 5%)`,
      },
      ...(isActive && {
        color: theme.palette.primary.main,
        pointerEvents: "none",
      }),
    }),
    padding: size === "small" ? theme.spacing(6 / 8) : size === "large" ? theme.spacing(3) : theme.spacing(2.25),
  }),
}));

type TButtonProps = {
  children?: ReactNode;
  isLink?: boolean;
  variant?: "primary" | "secondary" | "error" | "outlined";
  size?: "small" | "standart" | "large";
  isLoading?: boolean;
  isActive?: boolean;
} & DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

export const Button = (props: TButtonProps) => {
  const {
    children,
    disabled,
    isLink,
    isLoading,
    isActive,
    variant = "primary",
    size = "standart",
    ...otherProps
  } = props;

  return (
    <SButton
      type="button"
      {...otherProps}
      disabled={disabled || isLoading}
      isActive={isActive}
      size={size}
      variant={variant}
      isLink={isLink}
    >
      {isLoading ? <CircularProgress color="inherit" size={18} /> : <>{children}</>}
    </SButton>
  );
};
